// Per-map ambience engine: layered beds (wind, hum, hiss…) plus sparse one-shot critters and events, all procedural.
// Beds are looping stereo noise / steady oscillators through a filter; their level follows day / night (the live cycle
// crossfades), the storm level (global fog hazards) and slow random gusts. One-shots run on a small pool of PERSISTENT
// voices per event kind (oscillator or noise -> filter -> gain -> pan): an event only schedules automation on a free voice,
// so nothing is allocated per event and the pool size is the hard cap on overlapping sounds of that kind.
import type { AmbienceSpec, BedSpec, EventKind, EventSpec, NoiseColor } from './mapAudioDef';
import { Kit, tc } from './kit';

type VType = 'tone' | 'fm' | 'noise';
interface KindDef { v: VType; wave?: OscillatorType; noise?: NoiseColor; filter: BiquadFilterType; f: number; q?: number; sweepPan?: boolean }
const KIND: Record<EventKind, KindDef> = {
  bird: { v: 'fm', wave: 'sine', filter: 'lowpass', f: 9000 },
  songbird: { v: 'fm', wave: 'sine', filter: 'lowpass', f: 9000 },
  cricket: { v: 'tone', wave: 'sine', filter: 'lowpass', f: 9000 },
  owl: { v: 'tone', wave: 'sine', filter: 'lowpass', f: 1100 },
  raptor: { v: 'fm', wave: 'sawtooth', filter: 'bandpass', f: 2300, q: 1.6 },
  coo: { v: 'tone', wave: 'sine', filter: 'lowpass', f: 1300 },
  flap: { v: 'noise', noise: 'pink', filter: 'bandpass', f: 700, q: 1.2, sweepPan: true },
  howl: { v: 'fm', wave: 'sawtooth', filter: 'lowpass', f: 1250 },
  insect: { v: 'fm', wave: 'sawtooth', filter: 'bandpass', f: 1800, q: 2, sweepPan: true },
  call: { v: 'fm', wave: 'sine', filter: 'lowpass', f: 3500 },
  croak: { v: 'fm', wave: 'sawtooth', filter: 'bandpass', f: 650, q: 3 },
  whale: { v: 'fm', wave: 'sine', filter: 'lowpass', f: 900 },
  siren: { v: 'tone', wave: 'triangle', filter: 'lowpass', f: 1300 },
  creak: { v: 'fm', wave: 'sawtooth', filter: 'bandpass', f: 1100, q: 6 },
  spark: { v: 'noise', noise: 'white', filter: 'highpass', f: 2600 },
  pass: { v: 'noise', noise: 'pink', filter: 'bandpass', f: 300, q: 0.9, sweepPan: true },
  chime: { v: 'fm', wave: 'sine', filter: 'lowpass', f: 9000 },
  icezing: { v: 'fm', wave: 'sine', filter: 'lowpass', f: 7000 },
  drip: { v: 'tone', wave: 'sine', filter: 'highpass', f: 300 },
  bubble: { v: 'tone', wave: 'sine', filter: 'lowpass', f: 900 },
  rumble: { v: 'noise', noise: 'brown', filter: 'lowpass', f: 160 },
  thunder: { v: 'noise', noise: 'brown', filter: 'lowpass', f: 350 },
  hiss: { v: 'noise', noise: 'white', filter: 'bandpass', f: 4500, q: 0.8 },
  crack: { v: 'noise', noise: 'white', filter: 'bandpass', f: 2600, q: 1.4 },
  rockfall: { v: 'noise', noise: 'white', filter: 'bandpass', f: 1200, q: 1.2 },
  gust: { v: 'noise', noise: 'pink', filter: 'bandpass', f: 600, q: 0.8, sweepPan: true },
};

interface Voice {
  o: OscillatorNode | null; m: OscillatorNode | null; mg: GainNode | null;
  f: BiquadFilterNode; a: GainNode; p: StereoPannerNode; busy: number;
}
type R = () => number;
/** schedules one event on voice v at t (gain g, pitch k); returns when it has died away */
type EventFn = (v: Voice, t: number, g: number, k: number, r: R, spread: number) => number;

const lerp = (a: number, b: number, u: number) => a + (b - a) * u;
/** a short amplitude blip: rise in ~atk, hold, decay over ~rel (continuous: no clicks) */
function blip(a: AudioParam, t: number, peak: number, atk: number, hold: number, rel: number): number {
  a.setTargetAtTime(peak, t, tc(atk)); a.setTargetAtTime(0, t + atk + hold, tc(rel)); return t + atk + hold + rel;
}
function swell(a: AudioParam, t: number, peak: number, up: number, hold: number, down: number): number {
  a.setTargetAtTime(peak, t, tc(up)); a.setTargetAtTime(0, t + up + hold, tc(down)); return t + up + hold + down;
}
const sweepPan = (v: Voice, t: number, d: number, r: R, spread: number) => {
  const s = (r() < 0.5 ? -1 : 1) * spread * (0.5 + r() * 0.5);
  v.p.pan.setValueAtTime(s, t); v.p.pan.linearRampToValueAtTime(-s * (0.3 + r() * 0.7), t + d);
};

const EV: Record<EventKind, EventFn> = {
  bird(v, t, g, k, r) {
    const n = 2 + Math.floor(r() * 4), base = (2600 + r() * 1600) * k, up = r() < 0.5; let s = t;
    v.m!.frequency.setValueAtTime(35 + r() * 40, t); v.mg!.gain.setValueAtTime(base * 0.03, t);
    for (let i = 0; i < n; i++) {
      const d = 0.05 + r() * 0.07, f0 = base * (0.85 + r() * 0.35), f1 = f0 * (up ? 1.3 + r() * 0.4 : 0.6 + r() * 0.2);
      v.o!.frequency.setValueAtTime(f0, s); v.o!.frequency.exponentialRampToValueAtTime(f1, s + d);
      blip(v.a.gain, s, g * (0.7 + r() * 0.3), 0.008, d * 0.6, 0.03);
      s += d + 0.04 + r() * 0.12;
    }
    return s + 0.1;
  },
  songbird(v, t, g, k, r) {
    const base = (2200 + r() * 1400) * k, set = [1, 1.122, 1.26, 1.5, 0.841, 1.335], n = 6 + Math.floor(r() * 7); let s = t;
    v.m!.frequency.setValueAtTime(18 + r() * 14, t); v.mg!.gain.setValueAtTime(base * 0.015, t);
    for (let i = 0; i < n; i++) {
      if (r() < 0.2) { // a quick trill on two neighbouring notes
        const fa = base * set[Math.floor(r() * set.length)], fb = fa * 1.12;
        for (let j = 0; j < 5; j++) { v.o!.frequency.setValueAtTime(j % 2 ? fb : fa, s); blip(v.a.gain, s, g * 0.6, 0.004, 0.014, 0.012); s += 0.032; }
        s += 0.04; continue;
      }
      const d = 0.05 + r() * 0.07, f = base * set[Math.floor(r() * set.length)];
      v.o!.frequency.setValueAtTime(f, s); v.o!.frequency.linearRampToValueAtTime(f * (0.94 + r() * 0.14), s + d);
      blip(v.a.gain, s, g * (0.65 + r() * 0.35), 0.006, d * 0.7, 0.025);
      s += d + 0.02 + r() * 0.05;
    }
    return s + 0.1;
  },
  cricket(v, t, g, k, r) {
    const f = (4300 + r() * 900) * k, chirps = 4 + Math.floor(r() * 7), per = 0.32 + r() * 0.25, pulses = 3 + (r() < 0.5 ? 1 : 0);
    v.o!.frequency.setValueAtTime(f, t);
    for (let c = 0; c < chirps; c++) for (let p = 0; p < pulses; p++) {
      const ps = t + c * per + p * 0.024; v.a.gain.setTargetAtTime(g, ps, 0.002); v.a.gain.setTargetAtTime(0, ps + 0.012, 0.003);
    }
    return t + chirps * per + 0.05;
  },
  owl(v, t, g, k, r) {
    const f = (370 + r() * 60) * k, at = r() < 0.5 ? [0, 0.55, 0.78] : [0, 0.6]; let end = t;
    for (const o of at) {
      const s = t + o; v.o!.frequency.setValueAtTime(f * 1.04, s); v.o!.frequency.exponentialRampToValueAtTime(f * 0.93, s + 0.32);
      end = blip(v.a.gain, s, g, 0.06, 0.13, 0.14);
    }
    return end + 0.1;
  },
  raptor(v, t, g, k, r) {
    const n = r() < 0.4 ? 2 : 1; let s = t, end = t;
    for (let i = 0; i < n; i++) {
      const f0 = (2400 + r() * 500) * k, d = 0.55 + r() * 0.35;
      v.m!.frequency.setValueAtTime(26 + r() * 20, s); v.mg!.gain.setValueAtTime(f0 * 0.04, s);
      v.o!.frequency.setValueAtTime(f0 * 0.9, s); v.o!.frequency.linearRampToValueAtTime(f0, s + 0.08); v.o!.frequency.exponentialRampToValueAtTime(f0 * 0.68, s + d);
      v.f.frequency.setValueAtTime(f0, s);
      end = blip(v.a.gain, s, g, 0.05, d * 0.6, d * 0.35); s = end + 0.25;
    }
    return end + 0.1;
  },
  coo(v, t, g, k, r) {
    const f = (470 + r() * 70) * k; let s = t;
    for (const [m, d] of [[1, 0.3], [1.12, 0.17], [0.92, 0.4]]) {
      v.o!.frequency.setValueAtTime(f * m * 0.97, s); v.o!.frequency.linearRampToValueAtTime(f * m, s + d * 0.4); v.o!.frequency.linearRampToValueAtTime(f * m * 0.94, s + d);
      blip(v.a.gain, s, g, 0.05, d * 0.5, 0.08); s += d + 0.06;
    }
    return s + 0.15;
  },
  flap(v, t, g, k, r, sp) {
    const n = 4 + Math.floor(r() * 7), rate = 3.2 + r() * 2;
    v.f.frequency.setValueAtTime((520 + r() * 300) * k, t); sweepPan(v, t, n / rate, r, sp);
    for (let i = 0; i < n; i++) blip(v.a.gain, t + i / rate, g * (0.6 + r() * 0.4) * (1 - i / (n + 2)), 0.025, 0.03, 0.08);
    return t + n / rate + 0.2;
  },
  howl(v, t, g, k, r) {
    const f0 = (240 + r() * 60) * k, d = 2 + r() * 1.5;
    v.m!.frequency.setValueAtTime(5 + r() * 1.5, t); v.mg!.gain.setValueAtTime(0, t); v.mg!.gain.linearRampToValueAtTime(f0 * 0.025, t + 0.8);
    v.o!.frequency.setValueAtTime(f0 * 0.8, t); v.o!.frequency.exponentialRampToValueAtTime(f0 * 1.5, t + 0.6);
    v.o!.frequency.linearRampToValueAtTime(f0 * 1.42, t + d - 0.6); v.o!.frequency.exponentialRampToValueAtTime(f0, t + d);
    return swell(v.a.gain, t, g, 0.35, d - 0.9, 0.7) + 0.2;
  },
  insect(v, t, g, k, r, sp) {
    const f = (200 + r() * 250) * k, d = 0.8 + r() * 1.8;
    v.m!.frequency.setValueAtTime(40 + r() * 40, t); v.mg!.gain.setValueAtTime(f * 0.12, t);
    v.o!.frequency.setValueAtTime(f, t); v.o!.frequency.linearRampToValueAtTime(f * (0.9 + r() * 0.2), t + d);
    sweepPan(v, t, d, r, sp);
    return swell(v.a.gain, t, g, d * 0.4, d * 0.2, d * 0.4) + 0.1;
  },
  call(v, t, g, k, r) {
    const lo = (280 + r() * 200) * k, ratio = 1.5 + r() * 1.8, n = 2 + Math.floor(r() * 3); let s = t, end = t;
    for (let i = 0; i < n; i++) {
      const d = 0.25 + r() * 0.25, f = lo * (0.9 + r() * 0.25);
      v.o!.frequency.setValueAtTime(f, s); v.o!.frequency.exponentialRampToValueAtTime(f * (2 + r()), s + d * 0.5); v.o!.frequency.exponentialRampToValueAtTime(f * 1.1, s + d);
      v.m!.frequency.setValueAtTime(f * ratio, s); v.mg!.gain.setValueAtTime(f * 0.8, s); v.mg!.gain.setTargetAtTime(f * 0.1, s, d / 3);
      end = blip(v.a.gain, s, g, 0.04, d * 0.5, d * 0.4); s += d + 0.08 + r() * 0.2;
    }
    return end + 0.1;
  },
  croak(v, t, g, k, r) {
    const f = (110 + r() * 60) * k, n = 2 + Math.floor(r() * 4); let s = t, end = t;
    v.m!.frequency.setValueAtTime(22 + r() * 8, t); v.mg!.gain.setValueAtTime(f * 0.5, t); v.o!.frequency.setValueAtTime(f, t);
    for (let i = 0; i < n; i++) { const d = 0.12 + r() * 0.1; end = blip(v.a.gain, s, g, 0.01, d, 0.05); s += d + 0.12 + r() * 0.18; }
    return end + 0.1;
  },
  whale(v, t, g, k, r) {
    const f0 = (160 + r() * 90) * k, d = 3 + r() * 2;
    v.m!.frequency.setValueAtTime(4, t); v.mg!.gain.setValueAtTime(f0 * 0.012, t);
    v.o!.frequency.setValueAtTime(f0, t); v.o!.frequency.exponentialRampToValueAtTime(f0 * 1.6, t + d * 0.4);
    v.o!.frequency.exponentialRampToValueAtTime(f0 * 1.2, t + d * 0.7); v.o!.frequency.exponentialRampToValueAtTime(f0 * 1.45, t + d);
    return swell(v.a.gain, t, g, d * 0.3, d * 0.35, d * 0.4) + 0.3;
  },
  siren(v, t, g, k, r) {
    const d = 5 + r() * 3, lo = (560 + r() * 80) * k, hi = lo * 1.45, per = 2 + r() * 0.6, c = Math.floor(d / per);
    v.o!.frequency.setValueAtTime(lo, t);
    for (let i = 0; i < c; i++) { v.o!.frequency.linearRampToValueAtTime(hi, t + (i + 0.5) * per); v.o!.frequency.linearRampToValueAtTime(lo, t + (i + 1) * per); }
    return swell(v.a.gain, t, g, 1.5, c * per - 3, 2) + 0.3;
  },
  creak(v, t, g, k, r) {
    const d = 0.8 + r() * 1.4, f = (60 + r() * 70) * k, ff = (700 + r() * 900) * k, segs = 2 + Math.floor(r() * 2);
    v.m!.frequency.setValueAtTime(f * 1.414, t); v.mg!.gain.setValueAtTime(f * 0.4, t);
    v.o!.frequency.setValueAtTime(f, t); v.o!.frequency.linearRampToValueAtTime(f * 1.18, t + d * 0.5); v.o!.frequency.linearRampToValueAtTime(f * 0.92, t + d);
    v.f.frequency.setValueAtTime(ff, t); v.f.frequency.linearRampToValueAtTime(ff * 1.4, t + d);
    for (let i = 0; i < segs; i++) { const s = t + (i / segs) * d; v.a.gain.setTargetAtTime(g * (0.5 + r() * 0.5), s, tc(0.15)); v.a.gain.setTargetAtTime(g * 0.15, s + (d / segs) * 0.6, tc(0.2)); }
    v.a.gain.setTargetAtTime(0, t + d, tc(0.25));
    return t + d + 0.35;
  },
  spark(v, t, g, k, r) {
    const n = 6 + Math.floor(r() * 14); let s = t;
    v.f.frequency.setValueAtTime(2600 * k, t);
    for (let i = 0; i < n; i++) { blip(v.a.gain, s, g * (0.3 + r() * 0.7), 0.0015, 0.004, 0.012); s += 0.008 + r() * 0.045; }
    return s + 0.05;
  },
  pass(v, t, g, k, r, sp) {
    const d = 4 + r() * 3;
    v.f.frequency.setValueAtTime(220 * k, t); v.f.frequency.exponentialRampToValueAtTime(520 * k, t + d * 0.5); v.f.frequency.exponentialRampToValueAtTime(200 * k, t + d);
    sweepPan(v, t, d, r, sp);
    return swell(v.a.gain, t, g, d * 0.45, 0, d * 0.5) + 0.2;
  },
  chime(v, t, g, k, r) {
    const base = (1100 + r() * 500) * k, set = [1, 1.125, 1.25, 1.5, 1.6875, 2], n = 1 + Math.floor(r() * 3); let s = t, end = t;
    for (let i = 0; i < n; i++) {
      const f = base * set[Math.floor(r() * set.length)], ring = 1.6 + r();
      v.o!.frequency.setValueAtTime(f, s); v.m!.frequency.setValueAtTime(f * 2.76, s);
      v.mg!.gain.setTargetAtTime(f * 1.2, s, 0.002); v.mg!.gain.setTargetAtTime(0, s + 0.006, 0.2);
      v.a.gain.setTargetAtTime(g * (0.7 + r() * 0.3), s, 0.0015); v.a.gain.setTargetAtTime(0, s + 0.006, tc(ring));
      end = s + ring; s += 0.3 + r() * 0.5;
    }
    return end + 0.2;
  },
  icezing(v, t, g, k, r) {
    const f0 = (2800 + r() * 1200) * k, n = r() < 0.4 ? 2 : 1; let s = t, end = t;
    for (let i = 0; i < n; i++) {
      v.o!.frequency.setValueAtTime(f0, s); v.o!.frequency.exponentialRampToValueAtTime(f0 * 0.12, s + 0.35);
      v.m!.frequency.setValueAtTime(f0 * 0.5, s); v.mg!.gain.setValueAtTime(f0 * 0.15, s); v.mg!.gain.setTargetAtTime(0, s, 0.08);
      end = blip(v.a.gain, s, g, 0.003, 0.05, 0.3); s = end + 0.15;
    }
    return end + 0.1;
  },
  drip(v, t, g, k, r) {
    const n = 1 + Math.floor(r() * 3); let s = t, end = t;
    for (let i = 0; i < n; i++) {
      const f0 = (900 + r() * 900) * k; v.o!.frequency.setValueAtTime(f0, s); v.o!.frequency.exponentialRampToValueAtTime(f0 * 2.2, s + 0.03);
      end = blip(v.a.gain, s, g * (0.6 + r() * 0.4), 0.002, 0.008, 0.06); s += 0.15 + r() * 0.45;
    }
    return end + 0.05;
  },
  bubble(v, t, g, k, r) {
    const n = 2 + Math.floor(r() * 5); let s = t, end = t;
    for (let i = 0; i < n; i++) {
      const f0 = (70 + r() * 60) * k, d = 0.07 + r() * 0.05;
      v.o!.frequency.setValueAtTime(f0, s); v.o!.frequency.exponentialRampToValueAtTime(f0 * (3 + r() * 1.5), s + d);
      end = blip(v.a.gain, s, g * (0.6 + r() * 0.4), 0.006, 0.04, 0.05); s += 0.06 + r() * 0.3;
    }
    return end + 0.05;
  },
  rumble(v, t, g, k, r) {
    const d = 2.5 + r() * 2.5, f = (90 + r() * 80) * k;
    v.f.frequency.setValueAtTime(f, t); v.f.frequency.linearRampToValueAtTime(f * 1.6, t + d * 0.4); v.f.frequency.linearRampToValueAtTime(f, t + d);
    return swell(v.a.gain, t, g, d * 0.35, 0, d * 0.5) + 0.3;
  },
  thunder(v, t, g, k, r) {
    const n = 2 + Math.floor(r() * 3), d = 2 + r() * 2;
    v.f.frequency.setValueAtTime(500 * k, t); v.f.frequency.exponentialRampToValueAtTime(120 * k, t + d);
    blip(v.a.gain, t, g, 0.06, 0.15, 0.5);
    for (let i = 1; i <= n; i++) blip(v.a.gain, t + (i / (n + 1)) * d, g * (0.75 - i * 0.12), 0.15, 0.1, 0.6);
    v.a.gain.setTargetAtTime(0, t + d, tc(1));
    return t + d + 1.1;
  },
  hiss(v, t, g, k, r) {
    const d = 0.6 + r() * 1.6, f = (3500 + r() * 2500) * k;
    v.f.frequency.setValueAtTime(f, t); v.f.frequency.exponentialRampToValueAtTime(f * 0.7, t + d);
    return swell(v.a.gain, t, g, 0.15 + r() * 0.15, d, 0.6) + 0.1;
  },
  crack(v, t, g, k, r) {
    const n = 3 + Math.floor(r() * 7); let s = t, end = t;
    v.f.frequency.setValueAtTime((1500 + r() * 2500) * k, t);
    for (let i = 0; i < n; i++) { end = blip(v.a.gain, s, g * Math.pow(0.8, i) * (0.6 + r() * 0.4), 0.001, 0.003, 0.012); s += 0.01 + r() * 0.08; }
    return end + 0.05;
  },
  rockfall(v, t, g, k, r) {
    const n = 8 + Math.floor(r() * 11), f0 = (800 + r() * 1200) * k; let s = t, gap = 0.06 + r() * 0.09, end = t;
    v.f.frequency.setValueAtTime(f0, t);
    for (let i = 0; i < n; i++) {
      const u = i / n, env = Math.sin(Math.PI * Math.min(1, u * 1.3 + 0.1));
      v.f.frequency.setValueAtTime(f0 * lerp(1, 0.55, u) * (0.8 + r() * 0.4), s);
      end = blip(v.a.gain, s, g * env * (0.3 + r() * 0.7), 0.002, 0.006, 0.03 + r() * 0.04);
      gap *= u < 0.4 ? 0.85 : 1.12; s += gap * (0.6 + r() * 0.8);
    }
    return end + 0.05;
  },
  gust(v, t, g, k, r, sp) {
    const d = 2 + r() * 2.5;
    v.f.frequency.setValueAtTime(350 * k, t); v.f.frequency.exponentialRampToValueAtTime((1100 + r() * 700) * k, t + d * 0.45); v.f.frequency.exponentialRampToValueAtTime(400 * k, t + d);
    sweepPan(v, t, d, r, sp);
    return swell(v.a.gain, t, g, d * 0.4, 0, d * 0.5) + 0.2;
  },
};

interface Bed { spec: BedSpec; g: GainNode; f: BiquadFilterNode; gust: number; nextGust: number; target: number; fTarget: number }
interface Ev { spec: EventSpec; voices: Voice[]; next: number; fn: EventFn }

export class Ambience {
  private beds: Bed[] = [];
  private evs: Ev[] = [];
  private night = 0; private storm = 0; private nextCtl = 0;
  /** QA: events played, and the most event voices sounding at once */
  played = 0; peakVoices = 0;

  constructor(private kit: Kit, spec: AmbienceSpec, out: AudioNode) {
    const now = kit.ac.currentTime, r = kit.rand;
    const send = kit.echo(spec.echo, out);
    for (const b of spec.beds) {
      const f = kit.filter(b.filter, b.freq, b.q ?? 0.707), g = kit.gain(0);
      if (typeof b.src === 'string') kit.noise(b.src, b.rate ?? 1).connect(f);
      else for (const hz of b.src.freqs) kit.osc(b.src.wave, hz).connect(f);
      f.connect(g).connect(out);
      this.beds.push({ spec: b, g, f, gust: 1, nextGust: now, target: -1, fTarget: b.freq });
    }
    for (const e of spec.events) {
      const kd = KIND[e.kind], voices: Voice[] = [], n = Math.max(1, Math.min(3, e.voices ?? 1));
      for (let i = 0; i < n; i++) {
        const f = kit.filter(kd.filter, kd.f, kd.q ?? 0.707), a = kit.gain(0), p = kit.pan(0), es = kit.gain(e.echo ?? 0.15);
        let o: OscillatorNode | null = null, m: OscillatorNode | null = null, mg: GainNode | null = null;
        if (kd.v === 'noise') kit.noise(kd.noise!).connect(f);
        else {
          o = kit.osc(kd.wave!, 440); o.connect(f);
          if (kd.v === 'fm') { m = kit.osc('sine', 30); mg = kit.gain(0); m.connect(mg).connect(o.frequency); }
        }
        f.connect(a).connect(p); p.connect(out); p.connect(es).connect(send);
        voices.push({ o, m, mg, f, a, p, busy: 0 });
      }
      // first occurrences come early (a fresh arena should not sit silent for a minute)
      this.evs.push({ spec: e, voices, next: now + 0.5 + r() * e.every[0] * 0.5, fn: EV[e.kind] });
    }
  }

  setState(night: number, storm: number): void { this.night = night; this.storm = storm; }

  private weight(day: number | undefined, night: number | undefined, storm: number | undefined): number {
    return lerp(day ?? 1, night ?? 1, this.night) + (storm ?? 0) * this.storm;
  }

  /** schedule everything due before `horizon` (called every ~50 ms; small batches) */
  pump(now: number, horizon: number): void {
    const r = this.kit.rand;
    if (now >= this.nextCtl) { // control rate: bed levels, gusts (4 Hz)
      this.nextCtl = now + 0.25;
      for (const b of this.beds) {
        const s = b.spec, gs = s.gust;
        if (gs && now >= b.nextGust) {
          const u = r() * 2 - 1; b.gust = 1 + gs.depth * u; b.nextGust = now + lerp(gs.every[0], gs.every[1], r());
          if (gs.sweep) { b.fTarget = s.freq * Math.pow(gs.sweep, u); b.f.frequency.setTargetAtTime(b.fTarget, now, tc(b.nextGust - now)); }
        }
        const lvl = s.gain * this.weight(s.day, s.night, s.storm) * b.gust;
        if (Math.abs(lvl - b.target) > b.target * 0.01 + 1e-5) {
          b.g.gain.setTargetAtTime(lvl, now, b.target < 0 ? 0.6 : gs ? tc(Math.max(0.8, b.nextGust - now)) : 0.8); b.target = lvl;
        }
      }
    }
    let busy = 0;
    for (const e of this.evs) {
      for (const v of e.voices) if (v.busy > now) busy++;
      if (e.next >= horizon) continue;
      const w = this.weight(e.spec.day, e.spec.night, e.spec.storm);
      if (w < 0.03) { e.next = now + 2; continue; } // not at this time of day: look again in a bit
      const t = Math.max(e.next, now + 0.02);
      let v: Voice | null = null; for (const x of e.voices) if (x.busy <= t) { v = x; break; }
      if (!v) { let soon = Infinity; for (const x of e.voices) soon = Math.min(soon, x.busy); e.next = soon + 0.1; continue; } // voice cap
      const sp = e.spec.spread ?? 0.8;
      if (!KIND[e.spec.kind].sweepPan) v.p.pan.setValueAtTime((r() * 2 - 1) * sp, t);
      v.busy = e.fn(v, t, e.spec.gain * (0.55 + 0.45 * Math.min(1, w)), (e.spec.pitch ?? 1) * (0.97 + r() * 0.06), r, sp);
      this.played++;
      e.next = t + lerp(e.spec.every[0], e.spec.every[1], r()) / Math.min(1.5, w);
    }
    if (busy > this.peakVoices) this.peakVoices = busy;
  }
  get voiceCount(): number { let n = 0; for (const e of this.evs) n += e.voices.length; return n; }
}
